import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Inline from "../components/layout/Inline";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import SelectInput from "../components/forms/SelectInput";
import TextArea from "../components/forms/TextArea";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../components/data/Table";

import tableStyles from "../components/data/Table.module.css";

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const narrowShellStyle = {
  width: "100%",
  maxWidth: "980px",
  margin: "0 auto",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const piecesButtonStyle = {
  minWidth: "32px",
  height: "32px",
  border: "1px solid var(--color-border)",
  borderRadius: "6px",
  background: "var(--color-surface-muted)",
  cursor: "pointer",
};

const piecesCountStyle = {
  minWidth: "1px",
  textAlign: "center",
  fontWeight: "var(--font-weight-semibold)",
  fontVariantNumeric: "tabular-nums",
};

const normalizeApprovedMembers = (data) => {
  return (Array.isArray(data) ? data : []).map((member) => ({
    user_id: member.user_id,
    phone_number: member.phone_number,
    name: member.name,
    surname: member.surname,
  }));
};

const CreateABill = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [excursions, setExcursions] = useState([]);
  const [members, setMembers] = useState([]);

  const [form, setForm] = useState({
    excursion_id: "",
    entry_date: new Date().toISOString().split("T")[0],
    bill_date: new Date().toISOString().split("T")[0],
    amount: 0,
    currency: "TRL",
    definition: "",
  });

  const [payers, setPayers] = useState({});
  const [beneficiaries, setBeneficiaries] = useState({});
  const [beneficiaryMonetaryShares, setBeneficiaryMonetaryShares] = useState(
    {}
  );

  const [message, setMessage] = useState("");
  const [excursionCurrency, setExcursionCurrency] = useState("");

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const navigate = useNavigate();

  const payerInputRefs = useRef({});
  const definitionRef = useRef(null);

  useEffect(() => {
    const fetchExcursions = async () => {
      try {
        setLoadingPage(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          { headers }
        );

        const data = Array.isArray(res.data) ? res.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);

          const selected = data.find(
            (exc) => String(exc.excursion_id) === activeId
          );

          if (selected) {
            setForm((prev) => ({
              ...prev,
              excursion_id: activeId,
              currency: selected.excursion_currency || prev.currency,
            }));

            setExcursionCurrency(selected.excursion_currency || "");
          }
        }
      } catch (err) {
        console.error(err);
        setMessage(t("create_bill.errors.load_excursions"));
      } finally {
        setLoadingPage(false);
      }
    };

    if (user && token) {
      fetchExcursions();
    }
  }, [user, token, headers, t]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!form.excursion_id || !token) {
        setMembers([]);
        return;
      }

      const selected = excursions.find(
        (exc) => String(exc.excursion_id) === String(form.excursion_id)
      );

      if (!selected) {
        setMembers([]);
        return;
      }

      if (selected.status?.toLowerCase() !== "live") {
        setMembers([]);
        setMessage(t("create_bill.errors.excursion_locked"));
        return;
      }

      try {
        setLoadingMembers(true);
        setMessage("");

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/${form.excursion_id}/approved-members`,
          { headers }
        );

        const approvedMembers = normalizeApprovedMembers(res.data);
        setMembers(approvedMembers);
      } catch (err) {
        console.error("Failed to fetch approved members:", err);
        setMembers([]);
        setMessage(t("create_bill.errors.load_members"));
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [form.excursion_id, excursions, token, headers, t]);

  useEffect(() => {
    const totalUnit = Object.values(beneficiaries).reduce(
      (a, b) => a + Number(b || 0),
      0
    );
    const calc = {};

    for (const id in beneficiaries) {
      calc[id] =
        totalUnit > 0
          ? (Number(beneficiaries[id] || 0) / totalUnit) *
            (parseFloat(form.amount) || 0)
          : 0;
    }

    setBeneficiaryMonetaryShares(calc);
  }, [beneficiaries, form.amount]);

  useEffect(() => {
    if (!user || members.length === 0) return;

    const p = {};
    const b = {};
    const amt = parseFloat(form.amount) || 0;

    members.forEach((m) => {
      const mid = String(m.user_id);
      p[mid] = m.user_id === user.user_id ? amt : 0;
      b[mid] = 1;
    });

    setPayers(p);
    setBeneficiaries(b);
  }, [members, user]);

  useEffect(() => {
    if (!user) return;

    const uid = String(user.user_id);

    setPayers((prev) => ({
      ...prev,
      [uid]: parseFloat(form.amount) || 0,
    }));
  }, [form.amount, user]);

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(form.excursion_id)
  );

  const isExcursionLocked =
    selectedExcursionData?.status?.toLowerCase() !== "live";

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.excursion_id} - ${selectedExcursionData.short_name} (${selectedExcursionData.start_date} ${t("create_bill.common.to")} ${selectedExcursionData.end_date})`
    : loadingPage
    ? t("create_bill.loading_excursions")
    : t("create_bill.no_excursion");

  const handleSubmit = async () => {
    if (!form.excursion_id) {
      alert(t("create_bill.errors.excursion_missing"));
      return;
    }

    if (isExcursionLocked) {
      setMessage(t("create_bill.errors.excursion_locked"));
      return;
    }

    const payerSum = Object.values(payers).reduce(
      (a, b) => a + parseFloat(b || 0),
      0
    );
    const amt = parseFloat(form.amount) || 0;

    if (Math.abs(payerSum - amt) > 0.01) {
      alert(t("create_bill.errors.payer_sum_mismatch"));
      return;
    }

    const payload = {
      ...form,
      amount: amt,
      excursion_id: parseInt(form.excursion_id, 10),
      payers: Object.entries(payers).map(([uid, share]) => ({
        user_id: parseInt(uid, 10),
        share,
      })),
      beneficiaries: Object.entries(beneficiaries).map(([uid, unit]) => ({
        user_id: parseInt(uid, 10),
        unit_share: unit,
        monetary_share: beneficiaryMonetaryShares[uid] || 0,
      })),
    };

    try {
      setIsSubmitting(true);

      await axios.post(`${process.env.REACT_APP_API_URL}/bills/b`, payload, {
        headers,
      });

      alert(t("create_bill.success"));
      navigate("/my-summary");
    } catch (err) {
      console.error(err);
      setMessage(t("create_bill.errors.create_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const focusNextPayer = (currentMid) => {
    const orderedMids = members.map((m) => String(m.user_id));
    const idx = orderedMids.indexOf(String(currentMid));

    if (idx >= 0 && idx < orderedMids.length - 1) {
      const nextMid = orderedMids[idx + 1];
      payerInputRefs.current[nextMid]?.focus?.();
      return;
    }

    if (definitionRef.current?.focus) {
      definitionRef.current.focus();
      return;
    }

    payerInputRefs.current[String(currentMid)]?.blur?.();
  };

  const payerSum = Object.values(payers).reduce(
    (a, b) => a + parseFloat(b || 0),
    0
  );
  const amtForMsg = parseFloat(form.amount) || 0;

  const payerMsg =
    Math.abs(payerSum - amtForMsg) < 0.01
      ? t("create_bill.validation.ok")
      : t("create_bill.validation.not_ok");

  if (loadingUser || loadingPage) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("create_bill.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("create_bill.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("create_bill.errors.no_session")}
          </div>
        }
      />
    );
  }

  const isEligible =
    user.role === "admin" ||
    (user.role === "member" && user.subscription_status === "approved");

  if (!isEligible) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("create_bill.title")} mode="main" />}
      >
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("create_bill.errors.not_allowed")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen
      topNav={<TopNav title={t("create_bill.title")} mode="main" />}
      bottomAction={
        <Button
          fullWidth
          size="lg"
          onClick={handleSubmit}
          disabled={isSubmitting || isExcursionLocked}
        >
          {isSubmitting
            ? t("create_bill.actions.creating")
            : t("create_bill.actions.create")}
        </Button>
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="5">
            <Card>
              <Stack gap="4">
                <FormField label={t("create_bill.fields.excursion")}>
                  <input
                    type="text"
                    value={selectedExcursionLabel}
                    disabled
                    readOnly
                    style={disabledInputStyle}
                  />
                </FormField>
              </Stack>
            </Card>

            <Card
              title={t("create_bill.sections.details")}
              subtitle={t("create_bill.sections.details_sub")}
            >
              <Stack gap="4">
                <Inline gap="4" wrap>
                  <div style={{ flex: "1 1 220px" }}>
                    <FormField label={t("create_bill.fields.entry_date")}>
                      <TextInput
                        type="date"
                        value={form.entry_date}
                        disabled={isExcursionLocked}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            entry_date: e.target.value,
                          }))
                        }
                      />
                    </FormField>
                  </div>

                  <div style={{ flex: "1 1 220px" }}>
                    <FormField label={t("create_bill.fields.bill_date")}>
                      <TextInput
                        type="date"
                        value={form.bill_date}
                        disabled={isExcursionLocked}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            bill_date: e.target.value,
                          }))
                        }
                      />
                    </FormField>
                  </div>
                </Inline>

                <Inline gap="4" wrap>
                  <div style={{ flex: "1 1 220px" }}>
                    <FormField label={t("create_bill.fields.amount")}>
                      <TextInput
                        type="number"
                        inputMode="decimal"
                        value={form.amount}
                        disabled={isExcursionLocked}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            definitionRef.current?.focus?.();
                          }
                        }}
                      />
                    </FormField>
                  </div>

                  <div style={{ flex: "1 1 220px" }}>
                    <FormField label={t("create_bill.fields.currency")}>
                      <SelectInput value={form.currency} disabled>
                        <option value={form.currency}>
                          {excursionCurrency || form.currency || "--"}
                        </option>
                      </SelectInput>
                    </FormField>
                  </div>
                </Inline>

                <FormField label={t("create_bill.fields.definition")}>
                  <TextArea
                    ref={definitionRef}
                    value={form.definition}
                    disabled={isExcursionLocked}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        definition: e.target.value,
                      }))
                    }
                    rows={4}
                  />
                </FormField>
              </Stack>
            </Card>

            {loadingMembers ? (
              <Card title={t("create_bill.sections.participants")}>
                <div>{t("create_bill.loading_members")}</div>
              </Card>
            ) : null}

            {members.length > 0 ? (
              <Card
                title={t("create_bill.sections.participants")}
                subtitle={t("create_bill.sections.participants_sub")}
              >
<TableShell>
  <Table>
    <thead>
      <tr>
        <Th columnIndex={0} sticky>
          {t("create_bill.table.member")}
        </Th>

        <Th align="right">
          {t("create_bill.table.paid")}
        </Th>

        <Th>
          {t("create_bill.table.share")}
        </Th>

        <Th align="right">
          {t("create_bill.table.equals")}
        </Th>
      </tr>
    </thead>

    <tbody>
      {members.map((m) => {
        const mid = String(m.user_id);
        const unit = beneficiaries[mid] || 0;
        const total = Object.values(beneficiaries).reduce(
          (a, b) => a + Number(b || 0),
          0
        );

        const monetary =
          total > 0
            ? (
                (unit / total) *
                (parseFloat(form.amount) || 0)
              ).toFixed(2)
            : "0.00";

        return (
          <tr key={m.user_id}>
            <Td columnIndex={0} sticky title={`${m.name} ${m.surname}`}>
              {m.name} {m.surname}
            </Td>

            <NumberTd className={tableStyles.tightPadding}>
              <TextInput
                ref={(el) => {
                  payerInputRefs.current[mid] = el;
                }}
                type="number"
                inputMode="decimal"
                step="0.01"
                enterKeyHint="next"
                value={payers[mid] ?? 0}
                disabled={isExcursionLocked}
                onChange={(e) =>
                  setPayers((prev) => ({
                    ...prev,
                    [mid]: parseFloat(e.target.value) || 0,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    focusNextPayer(mid);
                  }
                }}
              />
            </NumberTd>

            <Td className={tableStyles.tightPadding}>
              <Inline gap="1" align="center">
                <button
                  type="button"
                  style={piecesButtonStyle}
                  disabled={isExcursionLocked}
                  onClick={() =>
                    setBeneficiaries((prev) => ({
                      ...prev,
                      [mid]: Math.max(0, Number(prev[mid] || 0) - 1),
                    }))
                  }
                >
                  -
                </button>

                <span style={piecesCountStyle}>{unit}</span>

                <button
                  type="button"
                  style={piecesButtonStyle}
                  disabled={isExcursionLocked}
                  onClick={() =>
                    setBeneficiaries((prev) => ({
                      ...prev,
                      [mid]: Number(prev[mid] || 0) + 1,
                    }))
                  }
                >
                  +
                </button>
              </Inline>
            </Td>

            <NumberTd>{monetary}</NumberTd>
          </tr>
        );
      })}
    </tbody>
  </Table>
</TableShell>

              </Card>
            ) : null}

            <Card title={t("create_bill.sections.validation")}>
              <strong>{payerMsg}</strong>
              {message ? <p style={{ marginTop: "0.75rem" }}>{message}</p> : null}
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default CreateABill;